import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { CargoLastEvent, LastEventIdle, LastEventReport } from 'src/app/core/interfaces/cargoLastEvent';
import { CargoLastEventService } from 'src/app/core/services/cargo-last-event.service';
import { FirebaseDatabase } from 'src/app/core/services/firebaseDatabase.service';

/** Cada cuánto se recalcula la antigüedad del último reporte, en milisegundos. */
const REFRESH_ELAPSED_MS = 30000;

/**
 * Franja de estado GPS en vivo del vehículo, sobre el mapa de seguimiento del servicio.
 *
 * Muestra el estado de reporte y el tiempo detenido por separado porque son ortogonales:
 * un vehículo puede estar reportando perfectamente y llevar tres horas detenido, y esa es
 * justamente la situación que el operador necesita ver.
 */
@Component({
  selector: 'app-vehicle-live-status',
  templateUrl: './vehicle-live-status.component.html',
  styleUrls: ['./vehicle-live-status.component.scss'],
  providers: [CargoLastEventService, FirebaseDatabase]
})
export class VehicleLiveStatusComponent implements OnChanges, OnDestroy {

  @Input() cargo: Cargo;
  /** Último evento recibido, para que el mapa pueda enriquecer el globo del marcador. */
  @Output() lastEventChange: EventEmitter<CargoLastEvent> = new EventEmitter<CargoLastEvent>();

  public event: CargoLastEvent = null;
  public report: LastEventReport;
  public idle: LastEventIdle;
  public source = '';
  public novelty = '';

  private subscription: Subscription;
  private elapsedTimer: any;

  constructor(private cargoLastEventService: CargoLastEventService) {
    this.refreshIndicators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.cargo) return;
    const previous = changes.cargo.previousValue;
    const current = changes.cargo.currentValue;
    if (previous && current && previous.id === current.id) return;
    this.listenLastEvent();
  }

  ngOnDestroy(): void {
    this.stopListening();
  }

  /** Hay algo que mostrar solo cuando el servicio ya recibió al menos un reporte GPS. */
  public get hasEvent(): boolean {
    return !!this.event;
  }

  private listenLastEvent(): void {
    this.stopListening();
    if (!this.cargo || !this.cargo.id) return;

    this.subscription = this.cargoLastEventService.listen(this.cargo.id).subscribe((event) => {
      this.event = event;
      this.refreshIndicators();
      this.lastEventChange.emit(event);
    });

    // La antigüedad tiene que seguir corriendo aunque no llegue nada de Firebase: si el GPS
    // deja de reportar no llega ningún evento nuevo, y es justo cuando el dato importa.
    this.elapsedTimer = setInterval(() => this.refreshIndicators(), REFRESH_ELAPSED_MS);
  }

  private stopListening(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private refreshIndicators(): void {
    this.report = this.cargoLastEventService.getReport(this.event);
    this.idle = this.cargoLastEventService.getIdle(this.event);
    this.source = this.cargoLastEventService.getSourceLabel(this.event);
    this.novelty = this.cargoLastEventService.getNovelty(this.event);
  }
}
